/**
 * The i18n layer. `react-i18next` with ICU MessageFormat, per `docs/I18N.md` §1.
 *
 * Replaces the hand-rolled `strings.ts` catalogue that stood in until the library
 * landed. Three properties of this setup are deliberate and worth reading before
 * changing any of them.
 *
 * **No network, ever.** No `i18next-http-backend`, no lazy `import()`, no language
 * detector that calls out. Catalogues are bundled at build time
 * ({@link ./catalogues}), so `init` completes synchronously and `t` works on the
 * first render. That is why `useSuspense` is off: there is nothing to wait for, and
 * a Suspense boundary around the ward list would be a loading state that can never
 * occur (`AGENTS.md` §3 rule 3).
 * The one request this layer's neighbour makes is to the box's own api, asking which of the
 * bundled languages to *offer* (`offeredLanguages.ts`, DECISIONS.md #56) — never for catalogue
 * content, and nothing waits on it.
 *
 * **No browser language detection.** `i18next-browser-languagedetector` is not
 * installed, and not for want of knowing about it. A Swedish ward tablet whose
 * browser happens to be set to English would silently render the *unreviewed*
 * English catalogue over the legally authoritative record (`I18N.md` §7: English is
 * a phase 0 placeholder). The language is Swedish until somebody chooses otherwise,
 * and choosing is `I18N.md` §6's selector — on the login screen, which is phase 1.
 * {@link setLanguage} is what that selector will call.
 *
 * **ICU, and one thing ICU must never be used for.** Plurals and variables go
 * through MessageFormat, which is the point — `I18N.md` §8 forbids concatenating
 * translated fragments, and `{occupied} av {total} platser belagda` is one message
 * where `t('occupancy') + ': ' + n` was three. But an ICU message with an explicit
 * `{x, number}` argument formats through Intl's number formatting, which under `sv`
 * renders `1234.5` as `1 234,5` — the comma decimal separator that
 * `format/clinicalDose.ts` exists to prevent (`AGENTS.md` §3 rule 7, §5). A bare
 * `{x}` is plain `String(value)` and is safe; `#` inside a plural is locale
 * formatted and is for counts only. `tests/catalogue.test.ts` enforces this on
 * every catalogue, because the `Intl` call happens inside a dependency where the
 * source scan in `tests/noLocaleFormatting.test.ts` cannot see it.
 */

import i18next from 'i18next'
import ICU from 'i18next-icu'
import { initReactI18next } from 'react-i18next'

import { DEFAULT_NAMESPACE, languages, namespaces, REFERENCE_LANGUAGE, resources } from './catalogues'

/**
 * Keeps the document's `lang` attribute on the selected language.
 *
 * Not cosmetic: `lang` drives hyphenation and, more importantly, how a screen
 * reader pronounces the page. A Swedish patient name read out by an English speech
 * synthesiser is a name a clinician may not recognise.
 *
 * `index.html` still ships `lang="sv"` as its static value, which is correct and
 * must stay — it is what applies before this module has run, and an element with no
 * `lang` at all is worse than one with the default. What must not be touched there
 * is `translate="no"` and the `notranslate` meta: those are a patient safety
 * control, not a preference (`I18N.md` §2).
 */
function syncDocumentLanguage(language: string): void {
  // Guarded because the unit suite runs in node, with no DOM. The alternative is a
  // jsdom dependency to set one attribute.
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language
  }
}

void i18next
  .use(ICU)
  .use(initReactI18next)
  .init({
    resources,
    lng: REFERENCE_LANGUAGE,
    fallbackLng: REFERENCE_LANGUAGE,
    supportedLngs: languages,
    ns: namespaces,
    defaultNS: DEFAULT_NAMESPACE,

    // React escapes what it renders, so i18next escaping on top of it would
    // double-escape an apostrophe in a Swedish label.
    interpolation: { escapeValue: false },

    react: { useSuspense: false },

    // Off in production for the same reason the API returns codes and not text: a
    // missing key is a defect to fix in the catalogue, not a console message to
    // read on a ward. `tests/catalogue.test.ts` is what actually catches it, before
    // an image exists.
    debug: false,
  })

syncDocumentLanguage(i18next.language)
i18next.on('languageChanged', syncDocumentLanguage)

/**
 * Switch interface language.
 *
 * What `I18N.md` §6's login-screen selector will call, and the only supported way
 * in — it keeps the `lang` attribute in step, which setting `i18next.language`
 * directly would not.
 *
 * Note what it deliberately does not do: **it does not touch how dates, doses or
 * units are rendered.** Those are fixed by `I18N.md` §3–4 regardless of language,
 * and they get *more* load-bearing as languages are added, not less — a "properly
 * localised" Spanish interface is exactly where `0.5 mg` would become `0,5 mg`.
 */
export async function setLanguage(language: string): Promise<void> {
  if (!languages.includes(language)) {
    throw new Error(
      `No catalogue for '${language}'. Available: ${languages.join(', ')}. ` +
        'A language is a directory under src/locales — see i18n/catalogues.ts.',
    )
  }
  await i18next.changeLanguage(language)
}

export { languageName, languages, REFERENCE_LANGUAGE } from './catalogues'
export { i18next }
